import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import isEqual from 'lodash/isEqual';
import {roundDecimal} from '~/shared/utils/math';
import {isArt, isText, isUpload} from '~/data/accessors/icon';
import DesignIconConstants from '~/constants/DesignIconConstants';
// components
import FoundryImage from '~/shared/components/FoundryImage';
import ClipartImage from '~/shared/components/ClipartImage';
import UploadImage from '~/shared/components/UploadImage';

import './index.scss';

const noop = () => {};

const propTypes = {
  icon: PropTypes.instanceOf(Immutable.Record).isRequired,
  scale: PropTypes.number.isRequired,
  iconError: PropTypes.func,
  iconRect: PropTypes.object,
  iconRendered: PropTypes.func,
  margin: PropTypes.number,
  repositionForTextEffects: PropTypes.func,
  rerender: PropTypes.bool,
  selectedViewScaleFactor: PropTypes.number,
  style: PropTypes.object
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export default class DesignIcon extends Component {
  static displayName = 'DesignIcon';
  static propTypes = propTypes;

  static defaultProps = {
    iconError: noop,
    iconRendered: noop,
    repositionForTextEffects: noop
  };

  shouldComponentUpdate(nextProps) {
    return (
      !isEqual(this.props.icon, nextProps.icon) ||
      !isEqual(this.props.iconRect, nextProps.iconRect) ||
      !isEqual(this.props.style, nextProps.style) ||
      this.props.scale !== nextProps.scale
    );
  }

  handleError = err => {
    this.props.iconError(err);
  };

  checkHasCrop = icon => {
    const {crop} = icon.toJS();
    return (
      !!crop?.widthPercent &&
      !!crop?.heightPercent &&
      (crop?.widthPercent < 1 || crop?.heightPercent < 1)
    );
  };

  onTextRender = ({naturalWidth, naturalHeight}) => {
    const {icon, scale} = this.props;

    if (!icon.sizeIsDirty && icon.rendered) {
      return this.props.iconRendered({iconId: icon.id});
    }
    const width = roundDecimal(naturalWidth / scale);
    const height = roundDecimal(naturalHeight / scale);

    this.props.iconRendered({iconId: icon.id, width, height});

    if (icon.effectName === 'curve' && icon.textIsUpdating) {
      this.calculateRepositionForTextEffect(
        icon,
        roundDecimal(naturalHeight / scale)
      );
    }
  };

  onArtRender = ({naturalWidth, naturalHeight}) => {
    const {icon} = this.props;
    if (!icon.sizeIsDirty || !icon.lockRatio) {
      return this.props.iconRendered({iconId: icon.id});
    }
    if (icon.rotation) {
      const currentHeight = icon.height;
      const width = roundDecimal(
        currentHeight / (naturalHeight / naturalWidth),
        2
      );
      return this.props.iconRendered({
        iconId: icon.id,
        width: width,
        height: currentHeight
      });
    }
    this.calculateRatioAndRender({naturalWidth, naturalHeight});
  };

  onUploadRender = ({naturalWidth, naturalHeight}) => {
    const {icon} = this.props;
    if (!icon.sizeIsDirty) {
      return this.props.iconRendered({
        iconId: icon.id
      });
    }
    if (!icon.rendered) {
      return this.calculateRatioAndRender({naturalWidth, naturalHeight});
    }
    if (icon.rotation) {
      const currentWidth = icon.width;
      const height = roundDecimal(
        currentWidth / (naturalWidth / naturalHeight),
        2
      );
      return this.props.iconRendered({
        iconId: icon.id,
        width: currentWidth,
        height
      });
    }
    return this.calculateRatioAndRender({naturalWidth, naturalHeight});
  };

  calculateRatioAndRender = ({naturalWidth, naturalHeight}) => {
    const {icon} = this.props;

    if (this.checkHasCrop(icon)) {
      this.handleCroppedImage(icon, naturalWidth, naturalHeight);
      return;
    }

    this.handleUncroppedImage(icon, naturalWidth, naturalHeight);
  };

  handleCroppedImage = icon => {
    const {crop, originalImageHeight, originalImageWidth} = icon.toJS();
    const isAutoSize = icon.isAutoSize;
    const baseWidth = isAutoSize
      ? icon.naturalWidth
      : DesignIconConstants.UPLOAD.DEFAULTS.ICON_SIZE_INCHES;
    const baseHeight = isAutoSize
      ? icon.naturalHeight
      : DesignIconConstants.UPLOAD.DEFAULTS.ICON_SIZE_INCHES;

    const {width: newWidth, height: newHeight} = this.calculateCropDimensions(
      originalImageWidth,
      originalImageHeight,
      baseWidth,
      baseHeight,
      crop,
      isAutoSize
    );

    this.props.iconRendered({
      iconId: icon.id,
      width: newWidth,
      height: newHeight
    });
  };

  calculateCropDimensions = (
    originalWidth,
    originalHeight,
    baseWidth,
    baseHeight,
    crop,
    isAutoSize
  ) => {
    if (isAutoSize) {
      return {
        width: baseWidth * crop.widthPercent,
        height: baseHeight * crop.heightPercent
      };
    }

    const isPortrait = originalHeight > originalWidth;
    return {
      width: isPortrait
        ? ((baseWidth * originalWidth) / originalHeight) * crop.widthPercent
        : baseWidth * crop.widthPercent,
      height: isPortrait
        ? baseHeight * crop.heightPercent
        : ((baseHeight * originalHeight) / originalWidth) * crop.heightPercent
    };
  };

  handleUncroppedImage = (icon, naturalWidth, naturalHeight) => {
    const ratio = naturalWidth / naturalHeight;
    let width, height;

    if (icon.isAutoSize) {
      const scale = Math.min(
        icon.width / naturalWidth,
        icon.height / naturalHeight
      );
      width = naturalWidth * scale;
      height = naturalHeight * scale;
    } else {
      width = ratio > 1 ? icon.width : icon.height * ratio;
      height = ratio > 1 ? icon.width / ratio : icon.height;
    }

    this.props.iconRendered({iconId: icon.id, width, height});
  };

  calculateRepositionForTextEffect = (icon, newHeight) => {
    const height = icon.height;
    if (icon.effectSetting > 0) {
      this.props.repositionForTextEffects({
        iconId: icon.id,
        y: roundDecimal(icon.y - height / 2 + newHeight / 2)
      });
    } else {
      this.props.repositionForTextEffects({
        iconId: icon.id,
        y: roundDecimal(icon.y + height / 2 - newHeight / 2)
      });
    }
  };

  renderImage = () => {
    const iconProps = {
      handleError: this.handleError,
      options: this.props.icon,
      scale: this.props.scale
    };

    if (isArt(this.props.icon.type)) {
      return <ClipartImage {...iconProps} onRender={this.onArtRender} />;
    } else if (isUpload(this.props.icon.type)) {
      return <UploadImage {...iconProps} onRender={this.onUploadRender} />;
    } else {
      return (
        <FoundryImage
          {...iconProps}
          onRender={this.onTextRender}
          selectedViewScaleFactor={this.props.selectedViewScaleFactor}
        />
      );
    }
  };

  checkIfTextIconIsNotRendered = () => {
    const isZeroWidth = this.props.iconRect && this.props.iconRect.width === 0;
    const isZeroHeight =
      this.props.iconRect && this.props.iconRect.height === 0;
    return isZeroWidth && isZeroHeight;
  };

  getStyle = () => {
    const {icon, style, iconRect} = this.props;

    if (isText(icon.type) && this.checkIfTextIconIsNotRendered()) {
      return null;
    }

    return (
      style || {
        width: iconRect.width,
        height: iconRect.height,
        margin: `${DesignIconConstants.CHROME_OFFSET}px`
      }
    );
  };

  render() {
    const style = this.getStyle();
    const useCachedImage = this.cachedImage && !this.props.rerender;
    const image = (this.cachedImage = useCachedImage
      ? this.cachedImage
      : this.renderImage());
    return (
      <div className="ndx-Design-icon" style={style}>
        {image}
      </div>
    );
  }
}
